import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Grid,
  Chip,
  TextField,
  makeStyles,
  createStyles,
  Theme,
  IconButton,
  Avatar,
} from '@material-ui/core';
import { Autocomplete, Alert } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import {
  UserProfile,
  USER_ROLES,
  USER_INTERESTS,
  DEFAULT_USER_PROFILE,
} from './types';
import { Option } from '@internal/plugin-org-data-common';
import { useOrgContext, useOrgData } from '@internal/plugin-org-data-react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useSyncOrgPreferences } from './useSyncOrgPreferences';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      '& .MuiDialog-paper': {
        width: 920,
        minWidth: 920,
        maxWidth: 920,
        height: 'calc(100vh - 80px)',
        maxHeight: 750,
        overflow: 'hidden',
        borderRadius: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
          width: '95vw',
          minWidth: 'auto',
          height: 'calc(100vh - 60px)',
          maxHeight: 'none',
        },
      },
    },
    dialogTitle: {
      padding: theme.spacing(3, 4),
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      color: theme.palette.primary.contrastText,
      position: 'relative',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    titleText: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    subtitle: {
      marginTop: theme.spacing(0.5),
      fontSize: '0.875rem',
      opacity: 0.9,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2),
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    stepper: {
      padding: theme.spacing(2, 4, 1, 4),
      backgroundColor: 'transparent',
      '& .MuiStepIcon-root.MuiStepIcon-active': {
        color: theme.palette.primary.main,
      },
      '& .MuiStepIcon-root.MuiStepIcon-completed': {
        color: theme.palette.success.main,
      },
    },
    contentContainer: {
      height: 'calc(100% - 180px)',
      minHeight: 350,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    content: {
      padding: theme.spacing(2, 4, 3, 4),
      height: '100%',
      overflow: 'auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      '&::-webkit-scrollbar': {
        width: 8,
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.background.default,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.divider,
        borderRadius: 4,
      },
    },
    stepContent: {
      width: '100%',
      maxWidth: 800,
    },
    interestChip: {
      margin: theme.spacing(0.5),
      fontSize: '0.9rem',
      padding: theme.spacing(2, 1.5),
      transition: 'all 0.2s ease',
      '&.selected': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: theme.shadows[2],
      },
      '&:hover': {
        transform: 'scale(1.05)',
      },
    },
    welcomeAvatar: {
      width: 100,
      height: 100,
      marginBottom: theme.spacing(3),
      fontSize: 40,
      backgroundColor: theme.palette.primary.main,
      boxShadow: theme.shadows[4],
    },
    completeIcon: {
      fontSize: 64,
      color: theme.palette.success.main,
      marginBottom: theme.spacing(1.5),
      animation: '$scaleIn 0.5s ease-out',
    },
    '@keyframes scaleIn': {
      '0%': {
        transform: 'scale(0)',
        opacity: 0,
      },
      '50%': {
        transform: 'scale(1.1)',
      },
      '100%': {
        transform: 'scale(1)',
        opacity: 1,
      },
    },
    centerContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      minHeight: 'auto',
      height: '100%',
      width: '100%',
      maxWidth: 640,
      margin: '0 auto',
    },
    stepTitle: {
      marginBottom: theme.spacing(1),
      fontWeight: 600,
      fontSize: '1.2rem',
    },
    stepDescription: {
      marginBottom: theme.spacing(2),
      color: theme.palette.text.secondary,
      fontSize: '0.9rem',
    },
    actions: {
      padding: theme.spacing(2, 4, 2, 4),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
    },
    summaryItem: {
      padding: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
      borderLeft: `3px solid ${theme.palette.success.main}`,
      paddingLeft: theme.spacing(1.5),
    },
    successTitle: {
      fontWeight: 600,
      color: theme.palette.success.main,
      marginBottom: theme.spacing(1),
    },
    sectionTitle: {
      fontWeight: 600,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    interestSectionTitle: {
      fontWeight: 600,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(1.5),
    },
    captionText: {
      marginTop: theme.spacing(2),
      display: 'block',
    },
    summaryBox: {
      backgroundColor: theme.palette.action.hover,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      width: '100%',
      maxWidth: 400,
    },
    summaryTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1),
    },
    roleGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: theme.spacing(1.5),
      width: '100%',
      marginTop: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
    },
    roleCard: {
      padding: theme.spacing(1.5),
      border: `2px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1.5),
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: theme.palette.background.paper,
      textAlign: 'center',
      '&:hover': {
        borderColor: theme.palette.primary.light,
        backgroundColor: theme.palette.action.hover,
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[2],
      },
      '&.selected': {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}10`,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
      },
    },
    roleLabel: {
      fontWeight: 600,
      fontSize: '0.9rem',
      marginBottom: theme.spacing(0.25),
    },
    roleDescription: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.2,
    },
  }),
);

const STEPS = ['Welcome', 'Your Role', 'Organization & Interests', 'All Set!'];

interface ProfileSetupWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete: (profile: UserProfile) => void;
  initialProfile?: Partial<UserProfile>;
}

export const ProfileSetupWizard: React.FC<ProfileSetupWizardProps> = ({
  open,
  onClose,
  onComplete,
  initialProfile,
}) => {
  const classes = useStyles();
  const { filterPlatformsForDivisions, divisionOptions } = useOrgContext();
  const identityApi = useApi(identityApiRef);
  const { syncOrgPreferences } = useSyncOrgPreferences();

  // Get existing org preferences to sync with
  const {
    selectedDivisions: existingDivisions,
    selectedPlatforms: existingPlatforms,
    loading: orgLoading,
  } = useOrgData();

  const [activeStep, setActiveStep] = useState(0);
  const [profile, setProfile] = useState<Partial<UserProfile>>(() => ({
    ...DEFAULT_USER_PROFILE,
    ...initialProfile,
  }));
  const [orgInitialized, setOrgInitialized] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userPicture, setUserPicture] = useState<string | undefined>();
  const [syncError, setSyncError] = useState<string | null>(null);

  // Fetch user profile info
  useEffect(() => {
    let isActive = true;
    identityApi
      .getProfileInfo()
      .then(info => {
        if (isActive) {
          setUserDisplayName(info.displayName || 'User');
          setUserPicture(info.picture);
        }
      })
      .catch(() => {
        if (isActive) {
          setUserDisplayName('User');
        }
      });
    return () => {
      isActive = false;
    };
  }, [identityApi]);

  // Initialize profile with existing org preferences when they load
  useEffect(() => {
    if (!orgLoading && !orgInitialized && open) {
      const hasExistingOrg =
        existingDivisions.length > 0 || existingPlatforms.length > 0;
      if (hasExistingOrg) {
        setProfile(prev => ({
          ...prev,
          divisions:
            (prev.divisions?.length ?? 0) > 0
              ? prev.divisions
              : existingDivisions,
          platforms:
            (prev.platforms?.length ?? 0) > 0
              ? prev.platforms
              : existingPlatforms,
        }));
      }
      setOrgInitialized(true);
    }
  }, [orgLoading, orgInitialized, open, existingDivisions, existingPlatforms]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setOrgInitialized(false);
      // Reset profile to initial state when dialog opens
      setProfile({
        ...DEFAULT_USER_PROFILE,
        ...initialProfile,
      });
    }
  }, [open, initialProfile]);

  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    setProfile(prev => ({ ...prev, ...updates }));
  }, []);

  const handleNext = async () => {
    if (activeStep === STEPS.length - 1) {
      const finalProfile: UserProfile = {
        ...DEFAULT_USER_PROFILE,
        ...profile,
        version: 1,
        setupCompleted: true,
        createdAt: profile.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Sync divisions and platforms to existing Preferences storage
      const syncResult = await syncOrgPreferences(
        finalProfile.divisions || [],
        finalProfile.platforms || [],
      );

      // If syncing org preferences failed, do not complete the wizard
      if (!syncResult.success) {
        setSyncError(
          syncResult.error?.message ||
            'We could not save your organization preferences. Please try again.',
        );
        return;
      }

      onComplete(finalProfile);
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setSyncError(null); // Clear any sync error when navigating back
    setActiveStep(prev => prev - 1);
  };

  const canProceed = useCallback(() => {
    switch (activeStep) {
      case 1: // Role selection - role is required
        return !!profile.role;
      default:
        return true;
    }
  }, [activeStep, profile.role]);

  const platformOptions = filterPlatformsForDivisions(profile.divisions || []);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Welcome
        return (
          <Box className={classes.stepContent}>
            <Box className={classes.centerContent}>
              <Avatar
                src={userPicture}
                alt={userDisplayName}
                className={classes.welcomeAvatar}
              >
                {!userPicture && userDisplayName.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h3" gutterBottom style={{ fontWeight: 600 }}>
                Welcome, {userDisplayName}! 👋
              </Typography>
              <Typography
                variant="h6"
                gutterBottom
                color="primary"
                style={{ fontWeight: 500 }}
              >
                Let's Create Your Personalized Experience
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{
                  maxWidth: 520,
                  marginTop: 16,
                  lineHeight: 1.6,
                  fontSize: '1rem',
                }}
              >
                We'll customize your dashboard to show the most relevant tools,
                content, and insights based on your role and interests. This
                quick setup takes less than 2 minutes.
              </Typography>
              <Typography
                variant="body2"
                style={{ marginTop: 24, fontStyle: 'italic', opacity: 0.7 }}
              >
                Don't worry, you can change these settings anytime!
              </Typography>
            </Box>
          </Box>
        );

      case 1: // Your Role (Required)
        return (
          <Box className={classes.stepContent}>
            <Box
              className={classes.centerContent}
              style={{
                justifyContent: 'flex-start',
                paddingTop: 24,
                maxWidth: 700,
              }}
            >
              <Typography variant="h5" className={classes.stepTitle}>
                What's Your Role?
              </Typography>
              <Typography variant="body1" className={classes.stepDescription}>
                This helps us show you the most relevant tools, content, and
                recommendations.
              </Typography>

              <Box className={classes.roleGrid}>
                {USER_ROLES.map(role => (
                  <Box
                    key={role.value}
                    className={`${classes.roleCard} ${
                      profile.role === role.value ? 'selected' : ''
                    }`}
                    onClick={() => updateProfile({ role: role.value })}
                  >
                    <Typography className={classes.roleLabel}>
                      {role.label}
                    </Typography>
                    <Typography className={classes.roleDescription}>
                      {role.description}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.captionText}
              >
                Select one role to continue
              </Typography>
            </Box>
          </Box>
        );

      case 2: // Organization & Interests (Optional)
        return (
          <Box className={classes.stepContent}>
            <Typography variant="h5" className={classes.stepTitle}>
              Organization & Interests
            </Typography>
            <Typography variant="body1" className={classes.stepDescription}>
              Help us personalize your experience further. All fields are
              optional.
            </Typography>

            {/* Organization Section */}
            <Typography variant="h6" className={classes.sectionTitle}>
              Organization Info
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={divisionOptions}
                  getOptionLabel={(option: Option) => option.label}
                  value={divisionOptions.filter(opt =>
                    profile.divisions?.includes(opt.value),
                  )}
                  onChange={(_, newValue) =>
                    updateProfile({ divisions: newValue.map(v => v.value) })
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Division(s)"
                      variant="outlined"
                      placeholder="Select your division(s)"
                      helperText="Optional: Filter content by division"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={platformOptions}
                  getOptionLabel={(option: Option) => option.label}
                  value={platformOptions.filter(opt =>
                    profile.platforms?.includes(opt.value),
                  )}
                  onChange={(_, newValue) =>
                    updateProfile({ platforms: newValue.map(v => v.value) })
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Platform(s)"
                      variant="outlined"
                      placeholder="Select your platform(s)"
                      helperText="Optional: Filter content by platform"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Team Name"
                  variant="outlined"
                  value={profile.squad || ''}
                  onChange={e =>
                    updateProfile({ squad: e.target.value || null })
                  }
                  placeholder="e.g., Platform Engineering, DevOps Team, Cloud Infrastructure"
                  helperText="Optional: Your team name"
                />
              </Grid>
            </Grid>

            {/* Interests Section */}
            <Typography variant="h6" className={classes.interestSectionTitle}>
              Your Interests
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select topics you'd like to stay updated on. We'll personalize
              recommendations based on your selections.
            </Typography>
            <Box>
              {USER_INTERESTS.map(interest => (
                <Chip
                  key={interest.value}
                  label={interest.label}
                  onClick={() => {
                    const current = profile.interests || [];
                    const updated = current.includes(interest.value)
                      ? current.filter(i => i !== interest.value)
                      : [...current, interest.value];
                    updateProfile({ interests: updated });
                  }}
                  className={`${classes.interestChip} ${
                    profile.interests?.includes(interest.value)
                      ? 'selected'
                      : ''
                  }`}
                  variant={
                    profile.interests?.includes(interest.value)
                      ? 'default'
                      : 'outlined'
                  }
                />
              ))}
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              className={classes.captionText}
            >
              💡 Tip: You can select multiple topics. Choose as many as you
              like!
            </Typography>
          </Box>
        );

      case 3: // Complete
        return (
          <Box className={classes.stepContent}>
            <Box className={classes.centerContent}>
              <CheckCircleIcon className={classes.completeIcon} />
              <Typography
                variant="h4"
                gutterBottom
                className={classes.successTitle}
              >
                You're All Set! 🎉
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Your personalized dashboard is ready! You'll now see content,
                tools, and recommendations tailored to your preferences.
              </Typography>
              <Box mt={2} textAlign="left" className={classes.summaryBox}>
                <Typography
                  variant="subtitle2"
                  className={classes.summaryTitle}
                >
                  Your Profile Summary:
                </Typography>
                {profile.role && (
                  <Box className={classes.summaryItem}>
                    <Typography variant="body2">
                      <strong>Role:</strong>{' '}
                      {USER_ROLES.find(r => r.value === profile.role)?.label}
                    </Typography>
                  </Box>
                )}
                {(profile.divisions?.length || 0) > 0 && (
                  <Box className={classes.summaryItem}>
                    <Typography variant="body2">
                      <strong>Division(s):</strong>{' '}
                      {profile.divisions
                        ?.map(
                          d =>
                            divisionOptions.find(opt => opt.value === d)
                              ?.label || d,
                        )
                        .join(', ')}
                    </Typography>
                  </Box>
                )}
                {(profile.platforms?.length || 0) > 0 && (
                  <Box className={classes.summaryItem}>
                    <Typography variant="body2">
                      <strong>Platform(s):</strong>{' '}
                      {profile.platforms
                        ?.map(
                          p =>
                            platformOptions.find(opt => opt.value === p)
                              ?.label || p,
                        )
                        .join(', ')}
                    </Typography>
                  </Box>
                )}
                {(profile.interests?.length || 0) > 0 && (
                  <Box className={classes.summaryItem}>
                    <Typography variant="body2">
                      <strong>Interests:</strong>{' '}
                      {profile.interests
                        ?.map(
                          i =>
                            USER_INTERESTS.find(int => int.value === i)
                              ?.label || i,
                        )
                        .join(', ')}
                    </Typography>
                  </Box>
                )}
                {profile.squad && (
                  <Box className={classes.summaryItem}>
                    <Typography variant="body2">
                      <strong>Team:</strong> {profile.squad}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant="caption"
                style={{ marginTop: 12, opacity: 0.7, fontStyle: 'italic' }}
              >
                You can update these preferences anytime from your profile menu
              </Typography>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth={false}
      disableEscapeKeyDown
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box>
          <Typography className={classes.titleText}>
            Dashboard Personalization Setup
          </Typography>
          <Typography className={classes.subtitle}>
            Step {activeStep + 1} of {STEPS.length}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          className={classes.closeButton}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box className={classes.contentContainer}>
        <Stepper
          activeStep={activeStep}
          className={classes.stepper}
          alternativeLabel
        >
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box className={classes.content}>{renderStepContent()}</Box>
      </Box>
      <DialogActions
        className={classes.actions}
        style={{ flexDirection: 'column', alignItems: 'stretch' }}
      >
        {syncError && (
          <Alert
            severity="error"
            onClose={() => setSyncError(null)}
            style={{ marginBottom: 16, width: '100%' }}
          >
            {syncError}
          </Alert>
        )}
        <Box display="flex" alignItems="center" width="100%">
          {activeStep < STEPS.length - 1 && (
            <Button
              onClick={() => setActiveStep(STEPS.length - 1)}
              color="default"
              disabled={!profile.role}
            >
              Skip to End
            </Button>
          )}
          <Box flex={1} />
          {activeStep > 0 && (
            <Button onClick={handleBack} size="large">
              Back
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={!canProceed()}
            size="large"
            style={{ minWidth: 120 }}
          >
            {activeStep === STEPS.length - 1 ? 'Get Started' : 'Next'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileSetupWizard;
